import React, { useState, useEffect, ReactElement, useRef, useCallback, KeyboardEvent, useMemo } from 'react';
import { actions as projectLocationActions } from '../../../../actions/projectLocationActions';
import { actions as projectManagerActions } from '../../../../actions/projectManagerActions';
import { connect, useDispatch } from 'react-redux';
import { bindActionCreators, type Dispatch } from 'redux';
import { DataTable, DataTableColReorderEvent } from 'primereact/datatable';
import { Paginator } from 'primereact/paginator';
import { Column } from 'primereact/column';
import { Project, LocationOption } from '@Types/common';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { DEFAULT_PAGE_SIZE } from '@Constants/constants';
import * as types from '@Constants/actionTypes';
import Notification from '@Common/Notification';
import Modal from '@Common/Modal/Modal';
import { Calendar } from 'primereact/calendar';
import { MultiSelect } from 'primereact/multiselect';
import { Dropdown } from 'primereact/dropdown';
import Modal61 from '@Common/Modal/Modal61';
import MultiSelectBox from '@Common/MultiSelectBox/MultiSelectBox';
import PacerButton from '@Common/Button';
import { PROJECT_LOCATION_BULK_UPDATE_OPTIONS as BulkOptions } from '@Constants/constants';
import AddToScopeDrawer from '../../../Subscription/Admin/ProjectLocations/AddToScopeDrawer';
import ChangeProjectManagerDrawer from '../../../Subscription/Admin/ProjectLocations/ChangeProjectManagerDrawer';
import AddBaselineDrawer from '../../../Subscription/Admin/ProjectLocations/AddBaselineDrawer';
import signalRService from '../../../../services/signalRService';
import { InfoWindowBody, InfoWindowTopicTitle, InfoWindowTopicBody, InfoWindowIconDescription, InfoWindowIcon, SelectAllCheck } from './styles';
import ProjectLocations from '../../../NewProjectWizard/ContentWidgets/ProjectLocations';
import { ProjectLocation } from '@Types/ProjectLocation';
import { MultiSelectOption } from '@Types/MultiSelectOption';
import { getMultiSelectFilterValues } from '@Helpers/multiselect';
import ChangeZeroDateDrawerModal from '@Components/Subscription/Admin/ProjectLocations/ChangeZeroDateDrawer/ChangeZeroDateDrawerModal';
import RadioGroup from '@Common/RadioGroup/RadioGroup';
import { LocationsProps, bulkOptions, ProjectLocationFilters, ProjectLocationColumn, SearchParams } from './Locations.types';

import { RemoveBaselineModal } from './components/RemoveBaseline/RemoveBaseline';
import ColumnsDrawer from './components/ColumnDrawer/ColumnDrawer';
import './Locations.scss';
import { ColumnOption } from '@Types/columns';
import { getStaticDefaultLocationColumns } from './locationColumnDefaults';
import { abbreviationsToArray, ACTIVE_FILTER_OPTIONS, formatDate, STATUS_FILTER_OPTIONS, initializeProjectLocationFilters } from './locationHelpers';

const Locations = ({ projectData, subscriptionId, userColumns, onColumnsChange }: LocationsProps): ReactElement => {
    const dispatch: Dispatch = useDispatch();

    // Helper function to format date in the expected format (D MMM YYYY)
    const [locations, setLocations] = useState<Array<ProjectLocation>>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedLocations, setSelectedLocations] = useState<Array<ProjectLocation>>([]);

    const getSelectedLocations = useCallback((): Array<ProjectLocation> => {
        return selectedLocations;
    }, [selectedLocations]);

    const [zeroDate, setZeroDate] = useState<Date | null>(null);
    const [selectedBaselines, setSelectedBaselines] = useState<Array<{ name: string; code: number; id: number }>>([]);
    const [projectManagers, setProjectManagers] = useState([]);
    const [projectBaselines, setProjectBaselines] = useState([]);
    const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
    const [selectedBulkOperation, setSelectedBulkOperation] = useState(BulkOptions.BulkUpdate.value);
    const [isAddToScopeDrawerOpen, setIsAddToScopeDrawerOpen] = useState(false);
    const [isChangeProjectManagerDrawerOpen, setIsChangeProjectManagerDrawerOpen] = useState(false);
    const [isColumnDrawerOpen, setIsColumnDrawerOpen] = useState<boolean>(false);
    const [isAddBaselineDrawerOpen, setIsAddBaselineDrawerOpen] = useState(false);
    const [isLoadingFormFilters, setIsLoadingFormFilters] = useState(false);
    const [showRemoveScopeWarning, setShowRemoveScopeWarning] = useState(false);
    const [showRevokePushdownWarning, setShowRevokePushdownWarning] = useState(false);
    const [showOnholdWarning, setShowOnholdWarning] = useState(false);
    const [projectManagersForDrawer, setpProjectManagersForDrawer] = useState([]);
    const [availableBulkOperations, setAvailableBulkOperations] = useState(bulkOptions);
    const [isHoldOperation, setIsHoldOperation] = useState(true);
    const [availLocations, setAvailLocations] = useState<Array<LocationOption>>([]);
    const [selectedLocationsModal, setSelectedLocationsModal] = useState<Array<LocationOption>>([]);
    const [isPublishing, setIsPublishing] = useState<Set<number>>(new Set());
    const [columnOptions, setColumnOptions] = useState<Array<ColumnOption<ProjectLocationColumn>>>([]);

    const [projectLocationListFilters, setProjectLocationListFilters] = useState<ProjectLocationFilters>({
        projectZeroDates: [],
        baselines: [],
        selectedBaselines: [],
        projectManagers: [],
        selectedProjectManagers: [],
        isOnHold: undefined,
        isActive: undefined,
        brands: [],
        continents: [],
        managementCompanies: [],
        areas: [],
        regions: [],
        types: [],
        countries: [],
        generation: '',
        locationAbbreviations: '',
        locationName: '',
        selectedStatus: 0,
    });

    const [projectManagersFilters, setProjectManagersFilters] = useState([]);
    const [typesOptions, setTypesOptions] = useState([]);
    const [zeroDateFilters, setZeroDateFilters] = useState<Array<any>>([]);
    const [baselines, setBaselines] = useState<Array<any>>([]);
    const [brands, setBrands] = useState<Array<any>>([]);
    const [continents, setContinents] = useState<Array<any>>([]);
    const [managementCompanies, setManagementCompanies] = useState<Array<any>>([]);
    const [regions, setRegions] = useState<Array<any>>([]);
    const [areas, setAreas] = useState<Array<any>>([]);
    const [countries, setCountries] = useState<Array<any>>([]);

    const [selectedProjectOwner, setSelectedProjectOwner] = useState(projectData.projectManagerId || null);
    const [sendEmailOnPublish, setSendEmailOnPublish] = useState<boolean>(true);

    // Change zero date modal data
    const [changeZeroDateData, setChangeZeroDateData] = useState<{
        selectedZeroDate: string;
        epicDates: { projectEpicLabelId: string; value: Date }[];
        selectedRadioOption: string;
        isFormValid: boolean;
    }>({
        selectedZeroDate: '',
        epicDates: [],
        selectedRadioOption: '',
        isFormValid: true,
    });

    const [searchParams, setSearchParams] = useState<SearchParams>({
        projectKey: projectData.projectKey,
        baselineIds: [],
        locationName: '',
        isActive: true,
        pageSize: DEFAULT_PAGE_SIZE,
        pageNumber: 1,
        sortBy: 'locationName',
        sortDir: 'asc',
        isOnHold: null,
        isPushedDown: null,
        projectZeroDates: [],
        locationAbbreviations: [],
        projectManagerIds: [],
        zeroDates: [],
        multiColumnSort: [
            {
                sortBy: 'locationName',
                sortDir: 'asc',
            },
        ],
    });

    const [totalCount, setTotalCount] = useState<number>(0);
    const [columns, setColumns] = useState<ProjectLocationColumn[]>([]);

    const SelectAllCheckbox = React.memo(() => {
        const isAllSelected = selectedLocations.length === locations.length && locations.length > 0;

        return (
            <SelectAllCheck>
                <Checkbox name='select-all' onChange={toggleSelectAll} checked={isAllSelected} />
                <span>Select All ({selectedLocations.length} selected)</span>
            </SelectAllCheck>
        );
    });

    const statusOptions = STATUS_FILTER_OPTIONS;

    const activeOptions = ACTIVE_FILTER_OPTIONS;

    const getLocations = async (params) => await dispatch(projectLocationActions.searchProjectLocations(params) as any);
    const addLocationToScope = async (params) => await dispatch(projectLocationActions.addToScope(params) as any);
    const loadProjectLocationListFilters = async (projectKey) =>
        await dispatch(projectLocationActions.loadProjectLocationListFilters(projectKey) as any);
    const getProjectManagers = async (params) => await dispatch(projectManagerActions.searchProjectManagers(params) as any);
    const removeFromScope = async (params) => await dispatch(projectLocationActions.removeFromScope(params) as any);
    const getBaselines = async (subscriptionId, projectKey) =>
        await dispatch(projectLocationActions.loadPushdownFilters(subscriptionId, projectKey) as any);
    const postPushdown = async (params) => await dispatch(projectLocationActions.pushdown(params) as any);
    const revokePushdown = async (locationIds: Array<number>, projectKey: string) =>
        await dispatch(projectLocationActions.revokePushdown(locationIds, projectKey) as any);
    const postOnHold = async (locationIds: Array<number>, projectKey: string, isOnHold: boolean) =>
        await dispatch(projectLocationActions.updateOnHold(locationIds, projectKey, isOnHold) as any);

    const publishModal = useRef<{ show: () => void; hide: () => void } | null>(null);
    const infoWindow = useRef<{ show: () => void; hide: () => void } | null>(null);
    const addProjectLocationsModal = useRef<{ show: () => void; hide: () => void } | null>(null);
    const changeZeroDateModal = useRef<{ show: () => void; hide: () => void } | null>(null);
    const removeBaselineModal = useRef<{ show: () => void; hide: () => void } | null>(null);

    const formatBaselines = async () => {
        const originalData = await getBaselines(subscriptionId, projectData.projectKey);

        const transformedData = originalData.payload.baselines.map((item) => ({
            name: item.name,
            code: item.id,
            id: item.id,
        }));

        return transformedData;
    };

    const formatProjectManagers = async () => {
        const originalData = await getProjectManagers({
            IsActive: true,
            PageSize: 0,
            SortBy: 'name',
        });

        setpProjectManagersForDrawer(originalData.payload.results);

        const transformedData = originalData.payload.results.map((item) => ({
            label: item.name,
            value: item.id,
        }));

        return transformedData;
    };

    const formatLoadProjectLocationListFilters = async () => {
        setIsLoadingFormFilters(true);
        const originalData = await loadProjectLocationListFilters(projectData.projectKey);
        const payload = originalData.payload;

        if (originalData.type === types.LOAD_PROJECTLOCATION_FILTERS_SUCCESS) {
            // Use the tested helper function to initialize filters correctly
            setProjectLocationListFilters(initializeProjectLocationFilters(projectLocationListFilters, payload));

            setProjectManagersFilters(payload.projectManagers);
            setTypesOptions(payload.types);
            setBaselines(payload.baselines);
            setBrands(payload.brands);
            setContinents(payload.continents);
            setManagementCompanies(payload.managementCompanies);
            setRegions(payload.regions);
            setAreas(payload.areas);
            setCountries(payload.countries);
            setZeroDateFilters(payload.projectZeroDates);
            setIsLoadingFormFilters(false);
        } else if (originalData.type === types.LOAD_PROJECTLOCATION_FILTERS_FAILURE) {
            Notification('error', 'An Error Occurred', payload.message);
            setIsLoadingFormFilters(false);
        }
    };

    const toggleAdvancedOptions = () => {
        setShowAdvancedOptions(!showAdvancedOptions);
    };

    const search = async (params: SearchParams) => {
        setIsLoading(true);
        try {
            const result = await getLocations(params);
            const payload = result.payload;
            if (result.type === types.SEARCH_PROJECTLOCATION_SUCCESS) {
                setSelectedBaselines([]);
                setSelectedLocations([]);
                setLocations(payload.results);
                setTotalCount(payload.totalCount);

                const currentProjectLocations = payload.results.map((item) => ({
                    abbreviation: item.locationAbbreviation,
                    id: item.locationId,
                    name: item.locationName,
                }));

                setSelectedLocationsModal(currentProjectLocations);
            } else if (result.type === types.SEARCH_PROJECTLOCATION_FAILURE) {
                Notification('error', 'An Error Occurred', payload.message);
            }
        } catch (error) {
            Notification('error', 'Error', error);
        } finally {
            setIsLoading(false);
        }
    };

    const removeFromScopeClick = async (row) => {
        try {
            const param = {
                projectLocationIds: [row.id],
                projectKey: projectData.projectKey,
            };
            const result = await removeFromScope(param);
            const payload = result.payload;

            if (result.type === types.REMOVE_FROM_PROJECTLOCATION_SCOPE_SUCCESS) {
                Notification('success', '', 'The location was removed from scope');
                search(searchParams);
            } else if (result.type === types.REMOVE_FROM_PROJECTLOCATION_SCOPE_FAILURE) {
                Notification('error', 'An Error Occurred', payload.message);
            }
        } catch (error) {
            Notification('error', 'Error', error);
        } finally {
            setIsLoading(false);
        }
    };

    const addLocationsToScope = async () => {
        const selectedLocationIds: number[] = selectedLocationsModal.map((item) => item.id);

        try {
            const params = {
                projectKey: projectData.projectKey,
                subscriptionId: subscriptionId,
                locationIds: selectedLocationIds,
            };
            const response = await addLocationToScope(params);
            if (response.type === types.ADD_TO_PROJECTLOCATION_SCOPE_SUCCESS) {
                Notification('success', '', `Locations added succesfully`);
                search(searchParams);
                addProjectLocationsModal.current?.hide();
            } else if (response.type === types.ADD_TO_PROJECTLOCATION_SCOPE_FAILURE) {
                Notification('error', 'An Error Occurred', response.payload.message);
            }
        } catch (error) {
            Notification('error', 'Error', error);
        } finally {
            setIsLoading(false);
        }
    };

    const openPublishModal = async (row: any) => {
        setSelectedLocations([row]);
        setSelectedBaselines([]);
        setZeroDate(null);
        setShowAdvancedOptions(false);
        publishModal.current?.show();
    };

    const onRevokePushdownIcon = async (row) => {
        try {
            setIsLoading(true);
            const response = await revokePushdown(
                [row].map((m) => m.id),
                projectData.projectKey
            );
            if (response.type === types.REVOKE_PUSHDOWN_PROJECTLOCATION_SUCCESS) {
                if (response.payload.skippedNonPushedLocations) {
                    setShowRevokePushdownWarning(true);
                }
                if (response.payload.changedSomeLocations) {
                    Notification('success', 'Success', 'Revoke Pushdown was successful');
                    search(searchParams);
                }
            } else if (response.type === types.REVOKE_PUSHDOWN_PROJECTLOCATION_FAILURE) {
                Notification('error', 'An Error Occurred', response.payload.message);
            }
        } catch (error) {
            Notification('error', 'An Error Occurred', error);
        } finally {
            setIsLoading(false);
        }
    };

    const onOpenLocationNewTab = (row) => {
        if (!row) {
            Notification('error', 'Invalid Location', 'This location does not have a valid key.');
            return;
        }

        if (!row.locationKey) {
            Notification('error', 'Invalid Location', 'This location does not have a valid key.');
            return;
        }

        if (!row.projectId) {
            Notification('error', 'Invalid Project', 'This location does not have a valid project.');
            return;
        }

        const url = `/tasks?l=${row.locationKey}&p=${row.projectId}`;
        window.open(url, '_blank');
    };

    const closeInfoWindow = async () => {
        infoWindow.current?.hide();
    };

    const UpdateOnHoldClick = async (row) => {
        try {
            setIsLoading(true);

            const result = await postOnHold([row.id], projectData.projectKey, !row.isOnHold);
            const payload = result.payload;

            if (result.type === types.UPDATE_PROJECTLOCATION_ONHOLD_SUCCESS) {
                const notificationMessage = row.isOnHold ? 'activated' : 'on-hold';

                Notification('success', '', `The Project Location was successfully ${notificationMessage}`);
                search(searchParams);
                closePublishModal();
            } else if (result.type === types.UPDATE_PROJECTLOCATION_ONHOLD_FAILURE) {
                Notification('error', 'An Error Occurred', payload.message);
            }
        } catch (error) {
            Notification('error', 'Error', error);
        } finally {
            setIsLoading(false);
        }
    };

    const onViewChange = async (e: string) => {
        setSelectedBulkOperation(e);
    };

    const onColumnsClick = () => {
        setIsColumnDrawerOpen(true);
    };

    const onDrawerClose = () => {
        setIsColumnDrawerOpen(false);
    };

    const onColumnToggle = async (field: string, isActive: boolean) => {
        const cols = columns.slice();
        const modifiedColumn = columnOptions.find((m) => m.value.field === field);

        if (isActive && modifiedColumn) {
            cols.splice(columns.length > 0 ? columns.length - 1 : 0, 0, modifiedColumn.value);
        } else if (modifiedColumn) {
            const removeIndex = cols.findIndex((c) => c.field === modifiedColumn.value.field);
            if (removeIndex !== -1) {
                cols.splice(removeIndex, 1);
            }
        }

        setColumns(cols);

        await onColumnsChange(
            cols.map((c) => c.field),
            false
        );
    };

    const buildColumnOptions = () => {
        const newColumns = getStaticProjectLocationColumns().slice();
        let selectedColumns: ProjectLocationColumn[] = [];
        // Look for newColumns personalization, else use the default
        if (userColumns?.length) {
            selectedColumns = userColumns
                .map((uc) => newColumns.find((c) => c.field === uc))
                .filter((col): col is ProjectLocationColumn => col !== undefined);
        } else {
            selectedColumns = newColumns.filter((c) => c.default);
        }

        setColumns(selectedColumns);
        setColumnOptions(
            newColumns.map((col) => ({
                label: col.optionLabel ?? col.header,
                value: col,
            }))
        );
    };

    const onRemoveFromScope = async () => {
        try {
            setIsLoading(true);
            const selectedLocations = getSelectedLocations();
            const request = {
                subscriptionId: subscriptionId,
                projectKey: projectData.projectKey,
                projectLocationIds: selectedLocations.map((x) => x.id),
            };
            const response = await removeFromScope(request);
            if (response.type === types.REMOVE_FROM_PROJECTLOCATION_SCOPE_SUCCESS) {
                if (response.payload.skippedPushedLocations) {
                    setShowRemoveScopeWarning(true);
                }
                if (response.payload.removedSomeLocations) {
                    Notification('success', 'Success', 'Remove from Scope completed successfully');
                    setSelectedLocations([]);
                    search(searchParams);
                }
            } else if (response.type === types.REMOVE_FROM_PROJECTLOCATION_SCOPE_FAILURE) {
                Notification('error', 'An Error Occurred', response.payload.message);
            }
        } catch (error) {
            Notification('error', 'Error', error);
        } finally {
            setIsLoading(false);
        }
    };

    const onChangeManagerIcon = async (row) => {
        const selectedLocation = locations.find((loc) => loc.id === row.id);
        if (selectedLocation) {
            setSelectedLocations([selectedLocation]);
        }
        setIsChangeProjectManagerDrawerOpen(true);
    };

    const onChangeZeroDateIcon = (row) => {
        // Use the row data directly as it contains the complete location object
        setSelectedLocations([row]);
        changeZeroDateModal.current?.show();
    };

    const onChangeZeroDateCompleteAndClear = async () => {
        await onChangeZeroDateComplete();
        setSelectedLocations([]);
    };

    const onsAddBaselineDrawerIcon = (row) => {
        const selectedLocation = locations.find((loc) => loc.id === row.id);
        if (selectedLocation) {
            setSelectedLocations([selectedLocation]);
        }
        setIsAddBaselineDrawerOpen(true);
    };

    const onChangeManager = async () => {
        setIsChangeProjectManagerDrawerOpen(true);
    };

    const onChangeZeroDate = () => {
        const selectedLocations = getSelectedLocations();
        if (selectedLocations.length === 0) {
            Notification('error', 'Locations Required', 'Please select at least one location to change the zero date.');
            return;
        }
        changeZeroDateModal.current?.show();
    };

    const onRemoveBaselineModalOpen = () => {
        const selectedLocations = getSelectedLocations();
        if (selectedLocations.length > 1 || selectedLocations.length === 0) {
            Notification('error', 'Single Location Required', 'Please select one location to remove the baseline.');
            return;
        }

        const selectedLocation = selectedLocations[0];

        if (!selectedLocation.baselines || selectedLocation.baselines.length <= 1) {
            Notification('error', 'Cannot Remove Baseline', 'Please select a location that has more than one baseline.');
            return;
        } else if (!selectedLocation.isPushedDown) {
            Notification('error', 'Cannot Remove Baseline', 'Baseline can only be removed from published locations.');
            return;
        } else if (selectedLocation.isOnHold) {
            Notification('error', 'Cannot Remove Baseline', 'Baseline cannot be removed from locations that are on hold.');
            return;
        }

        removeBaselineModal.current?.show();
    };

    const onRemoveBaselineModalHide = (refresh?: boolean) => {
        if (refresh) search(searchParams);

        removeBaselineModal.current?.hide();
    };

    const onRevokePushdown = async () => {
        try {
            setIsLoading(true);
            const selectedLocations = getSelectedLocations();
            const response = await revokePushdown(
                selectedLocations.map((m) => m.id).filter((id): id is number => id !== undefined),
                projectData.projectKey
            );
            if (response.type === types.REVOKE_PUSHDOWN_PROJECTLOCATION_SUCCESS) {
                if (response.payload.skippedNonPushedLocations) {
                    setShowRevokePushdownWarning(true);
                }
                if (response.payload.changedSomeLocations) {
                    Notification('success', 'Success', 'Revoke Pushdown was successful');
                    setSelectedLocations([]);
                    search(searchParams);
                }
            } else if (response.type === types.REVOKE_PUSHDOWN_PROJECTLOCATION_FAILURE) {
                Notification('error', 'An Error Occurred', response.payload.message);
            }
        } catch (error) {
            Notification('error', 'An Error Occurred', error);
        } finally {
            setIsLoading(false);
        }
    };

    const offHold = () => {
        updateOnHold(false);
    };

    const onHold = () => {
        updateOnHold(true);
    };

    const updateOnHold = async (isOnHold: boolean) => {
        setIsHoldOperation(isOnHold);
        try {
            const selectedLocations = getSelectedLocations();
            const response = await postOnHold(
                selectedLocations.map((m) => m.id).filter((id): id is number => id !== undefined),
                projectData.projectKey,
                isOnHold
            );
            if (response.type === types.UPDATE_PROJECTLOCATION_ONHOLD_SUCCESS) {
                if (response.payload.skippedNonPushedLocations) {
                    setShowOnholdWarning(true);
                }
                if (response.payload.changedSomeLocations) {
                    Notification('success', 'Success', 'Locations updated successfully');
                    setSelectedLocations([]);
                    search(searchParams);
                }
            } else if (response.type === types.UPDATE_PROJECTLOCATION_ONHOLD_FAILURE) {
                Notification('error', 'An Error Occurred', response.payload.message);
            }
        } catch (error) {
            Notification('error', 'An Error Occurred', error);
        }
    };

    const selectBulkOperation = async () => {
        if (selectedLocations.length === 0) return;

        switch (selectedBulkOperation) {
            case BulkOptions.BulkUpdate.value:
                break;
            case BulkOptions.AddBaseline.value:
                setIsAddBaselineDrawerOpen(true);
                break;
            case BulkOptions.RemoveBaseline.value:
                onRemoveBaselineModalOpen();
                break;
            case BulkOptions.Publish.value:
                setSelectedBaselines([]);
                publishModal.current?.show();
                break;
            case BulkOptions.RemoveScope.value:
                onRemoveFromScope();
                break;
            case BulkOptions.ChangeManager.value:
                onChangeManager();
                break;
            case BulkOptions.ChangeZeroDate.value:
                onChangeZeroDate();
                break;
            case BulkOptions.Unpublish.value:
                onRevokePushdown();
                break;
            case BulkOptions.OnHold.value:
                onHold();
                break;
            case BulkOptions.OffHold.value:
                offHold();
                break;
            default:
                break;
        }
    };

    const headerComponent = useMemo(() => {
        return (
            <div className='table-header-row'>
                <div className='table-header-buttons'>
                    <SelectAllCheckbox />
                    <MultiSelectBox
                        name='views'
                        idField='id'
                        valueField='name'
                        placeholder='Select Bulk Action'
                        className={'multi-select-bulk'}
                        options={availableBulkOperations}
                        onChange={onViewChange}
                        selectedValues={selectedBulkOperation}
                        isMulti={false}
                        isSearchable={false}
                    />
                    <Button label='Apply' onClick={selectBulkOperation} disabled={selectedLocations.length === 0} className='pacer-table-button' />
                </div>
                <Button icon='fa-regular fa-columns-3 fa-xl' title='Choose Columns' onClick={onColumnsClick} className='pacer-table-button' />
            </div>
        );
    }, [
        availableBulkOperations,
        selectedBulkOperation,
        selectedLocations.length,
        locations.length,
        onViewChange,
        selectBulkOperation,
        onColumnsClick,
    ]);

    // Function to handle "Select All" checkbox click
    const toggleSelectAll = useCallback(() => {
        if (selectedLocations.length === locations.length && locations.length > 0) {
            setSelectedLocations([]); // Clear all
        } else {
            setSelectedLocations([...locations]); // Select all
        }
    }, [locations, selectedLocations.length]);

    // Handler para mudanças de seleção do DataTable
    const onSelectionChange = useCallback((e) => {
        setSelectedLocations(e.value);
    }, []);

    const statusBodyTemplate = (rowData) => {
        let description = 'Draft';
        let color = 'icon-orange';

        if (rowData.isPushedDown === true && rowData.isOnHold === false) {
            description = 'Published';
            color = 'icon-green';
        }

        if (rowData.isOnHold === true) {
            description = 'On Hold';
            color = 'icon-black';
        }

        return (
            <div className='pacer-badge-container'>
                <span className='pacer-badge'>
                    <i className={`pi pi-circle-on ${color}`}></i>
                    <span className='pacer-status'>{description}</span>
                </span>
            </div>
        );
    };

    const zeroDateBodyTemplate = (rowData) => {
        let value = '-';

        if (rowData.zeroDate) {
            value = formatDate(rowData.zeroDate);
        }

        return <span>{value}</span>;
    };

    const isOnHoldTemplate = (rowData: ProjectLocation) => <span>{rowData.isOnHold ? 'Yes' : 'No'}</span>;
    const isActiveTemplate = (rowData: ProjectLocation) => <span>{rowData.isActive ? 'Yes' : 'No'}</span>;

    const baselineBodyTemplate = (rowData: ProjectLocation) => {
        if (rowData.baselines?.length === 0) {
            return <span>-</span>;
        }

        return (rowData.baselines ?? []).map((item) => <div key={item.id}>{item.name}</div>);
    };

    const actionBodyTemplate = (rowData) => {
        if (rowData.isPushedDown === true && rowData.isOnHold === false) {
            return (
                <>
                    <Button
                        icon='fa-regular fa-calendar-lines-pen'
                        onClick={() => onChangeZeroDateIcon(rowData)}
                        title='Change Zero Date'
                        className='fa-lg mx-2'
                    />
                    <Button
                        icon='fa-regular fa-square-list'
                        onClick={() => onsAddBaselineDrawerIcon(rowData)}
                        title='Add Baselines'
                        className='fa-lg mx-2'
                    />
                    <Button
                        icon='fa-regular fa-user-pen'
                        onClick={() => onChangeManagerIcon(rowData)}
                        title='Change Project Manager'
                        className='fa-lg mx-2'
                    />
                    <Button
                        icon='fa-regular fa-circle-pause'
                        onClick={() => UpdateOnHoldClick(rowData)}
                        title='Pause Project'
                        className='fa-lg mx-2'
                    />
                    <Button
                        icon='fa-regular fa-arrows-rotate'
                        onClick={() => onRevokePushdownIcon(rowData)}
                        title='Revoke Project'
                        className='fa-lg mx-2'
                    />
                    <Button
                        icon='fa-regular fa-list-check'
                        onClick={() => onOpenLocationNewTab(rowData)}
                        title='Open Location in new tab'
                        className='fa-lg mx-2'
                    />
                </>
            );
        }

        if (rowData.isPushedDown === true && rowData.isOnHold === true) {
            return (
                <>
                    <Button
                        icon='fa-regular fa-circle-play'
                        onClick={() => UpdateOnHoldClick(rowData)}
                        title='Unpause Project'
                        className='fa-lg mx-2'
                    />
                </>
            );
        }

        return (
            <>
                <Button icon='fa-regular fa-paper-plane-top' onClick={() => openPublishModal(rowData)} title='Publish' className='fa-lg mx-2' />
                <Button
                    icon='fa-regular fa-trash-can'
                    onClick={() => removeFromScopeClick(rowData)}
                    title='Remove from Scope'
                    className='fa-lg mx-2'
                    style={{ color: '#ef4444' }}
                />
            </>
        );
    };

    const onPageChange = async (e) => {
        const updatedParams = {
            ...searchParams,
            pageNumber: e.page + 1,
            pageSize: e.rows,
        };

        setSearchParams(updatedParams);
    };
    const onDropDownChange = (filterName: string, searchName: string, selectedValue: any) => {
        const updatedFilterParams = { ...projectLocationListFilters };
        const updatedSearchParams = { ...searchParams };

        updatedSearchParams[searchName] = selectedValue;
        updatedFilterParams[filterName] = selectedValue;

        setProjectLocationListFilters(updatedFilterParams);
        setSearchParams(updatedSearchParams);
    };
    const onMultiSelectChange = (filterName: string, searchName: string, selectedValues: any) => {
        // Create a copy of the current filters & searchParams (new object always triggers effect)
        const updatedFilterParams = { ...projectLocationListFilters };
        const updatedSearchParams = { ...searchParams };

        // Update the copies with the new property name and value
        // Convert objects to IDs for searchParams, but keep objects for filters
        updatedSearchParams[searchName] = getMultiSelectFilterValues(selectedValues);
        updatedFilterParams[filterName] = selectedValues; // Keep the objects directly

        setProjectLocationListFilters(updatedFilterParams);
        setSearchParams(updatedSearchParams);
    };

    const onStatusChange = (value) => {
        const updatedFilterParams = { ...projectLocationListFilters };
        updatedFilterParams.selectedStatus = value;
        setProjectLocationListFilters(updatedFilterParams);

        const updatedSearchParams = { ...searchParams };
        updatedSearchParams.isPushedDown = value == 0 ? null : value == 1 ? true : false;
        setSearchParams(updatedSearchParams);
    };

    const buildSearchRequest = (): SearchParams => {
        return {
            ...searchParams,
            locationName: projectLocationListFilters.locationName,
            locationAbbreviations: abbreviationsToArray(projectLocationListFilters.locationAbbreviations),
        };
    };

    const filterChange = (e: { target: { name: string; value: string } }) => {
        const name = e.target.name;
        const updatedParams = { ...projectLocationListFilters };
        updatedParams[name] = e.target.value;
        setProjectLocationListFilters(updatedParams);

        setSearchParams(buildSearchRequest());
    };

    const keyUpFilter = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            const target = event.target as HTMLInputElement;
            const name = target.name;
            const updatedParams = { ...projectLocationListFilters };
            updatedParams[name] = target.value;
            setProjectLocationListFilters(updatedParams);

            setSearchParams(buildSearchRequest());
        }
    };

    const onAddLocations = () => {
        addLocationsToScope();
    };

    const onPublishModal = async () => {
        const selectedLocations = getSelectedLocations();
        const locationIds = selectedLocations.map((location) => location.locationId).filter((id): id is number => id !== undefined);

        if (selectedBaselines.length === 0) {
            Notification('error', 'Validation Error', 'At least one Baseline is required');
            return;
        }

        if (!zeroDate) {
            Notification('error', 'Validation Error', 'Zero Date is required');
            return;
        }

        try {
            setIsLoading(true);

            setIsPublishing((prev) => new Set([...prev, ...locationIds]));

            const param = {
                projectManagerId: selectedProjectOwner,
                projectKey: projectData.projectKey,
                zeroDate: zeroDate ? formatDate(zeroDate) : '',
                BaselineIds: selectedBaselines.map((obj) => obj.code),
                LocationIds: locationIds,
                sendEmail: sendEmailOnPublish,
                daysSelected: [0, 1, 2, 3, 4, 5, 6],
            };

            const result = await postPushdown(param);
            const payload = result.payload;

            if (result.type === types.PUSHDOWN_PROJECTLOCATION_SUCCESS) {
                setSelectedLocations([]);
                closePublishModal();
                Notification(
                    'info',
                    'Publishing Project',
                    'We are publishing your project. This may take several minutes. You can move on to your next tasks in the meantime and we will email you when the project has published to all teams.'
                );
                search(searchParams);
            } else if (result.type === types.PUSHDOWN_PROJECTLOCATION_FAILURE) {
                setIsPublishing((prev) => {
                    const updated = new Set(prev);
                    locationIds.forEach((id) => updated.delete(id));
                    return updated;
                });

                Notification('error', 'An Error Occurred', payload.message);
            }
        } catch (error) {
            // Remove the locations from isPublishing
            setIsPublishing((prev) => {
                const updated = new Set(prev);
                locationIds.forEach((id) => updated.delete(id));
                return updated;
            });
            Notification('error', 'Error', error);
        } finally {
            setIsLoading(false);
        }
    };

    const onAddScopeCancel = () => {
        setIsAddToScopeDrawerOpen(false);
    };

    const isPublishFormValid = selectedBaselines.length > 0 && !!zeroDate;

    const onAddScopeComplete = () => {
        setIsAddToScopeDrawerOpen(false);
        search(searchParams);
    };

    const onChangeManagerComplete = () => {
        setIsChangeProjectManagerDrawerOpen(false);
        setSelectedLocations([]);
        search(searchParams);
    };

    const onChangeManagerCancel = () => {
        setIsChangeProjectManagerDrawerOpen(false);
    };

    const onAddBaselineCancel = () => {
        setIsAddBaselineDrawerOpen(false);
    };

    const onAddBaselineComplete = () => {
        setIsAddBaselineDrawerOpen(false);
        setSelectedLocations([]);
        search(searchParams);
    };

    const onChangeZeroDateComplete = async () => {
        const { selectedZeroDate, epicDates, selectedRadioOption, isFormValid } = changeZeroDateData;

        if (!selectedZeroDate) {
            Notification('error', 'Validation Error', 'Zero Date is required');
            return;
        }

        if (!isFormValid) {
            Notification('error', 'Validation Error', 'Please correct the form errors');
            return;
        }

        try {
            setIsLoading(true);

            // Convert epicDates Date objects to the format expected by the API
            const formattedEpicDates = epicDates.map((epicDate) => ({
                projectEpicLabelId: epicDate.projectEpicLabelId,
                value: formatDate(epicDate.value), // Convert Date to string
            }));

            const selectedLocations = getSelectedLocations();
            const response: any = await dispatch(
                projectLocationActions.updateZeroDate(
                    selectedLocations.map((m) => m.id).filter((id): id is number => id !== undefined),
                    projectData.projectKey,
                    selectedZeroDate,
                    formattedEpicDates,
                    Number(selectedRadioOption)
                ) as any
            );

            if (response.type === types.UPDATE_PROJECTLOCATION_ZERODATE_SUCCESS) {
                Notification('success', 'Success', 'Zero Date updated successfully');
                setSelectedLocations([]);
                search(searchParams);
                changeZeroDateModal.current?.hide();
            } else {
                Notification('error', 'An Error Occurred', response.payload.message);
            }
        } catch (error) {
            Notification('error', 'Error', String(error));
        } finally {
            setIsLoading(false);
        }
    };

    const onChangeZeroDateCancel = () => {
        changeZeroDateModal.current?.hide();
    };

    const closePublishModal = () => {
        setSelectedBaselines([]);
        setZeroDate(null);
        setShowAdvancedOptions(false);
        publishModal.current?.hide();
    };

    const closeAddLocationsModal = () => {
        addProjectLocationsModal.current?.hide();
    };

    const onChangeZeroDateDataChange = useCallback(
        (data: {
            selectedZeroDate: string;
            epicDates: { projectEpicLabelId: string; value: Date }[];
            selectedRadioOption: string;
            isFormValid: boolean;
        }) => {
            setChangeZeroDateData(data);
        },
        []
    );

    const onColumnReorder = async (e: DataTableColReorderEvent) => {
        // Save columns personalization
        const newColumns: Array<string | undefined> = e.columns.map((c) => c.props.field);
        await onColumnsChange(
            newColumns.filter((x) => x != null),
            true
        );
    };

    const closeRemoveScopeWarning = () => {
        setShowRemoveScopeWarning(false);
    };

    const closeRevokePushdownWarning = () => {
        setShowRevokePushdownWarning(false);
    };

    const closeOnholdWarning = () => {
        setShowOnholdWarning(false);
    };

    const getAdditionalBaselines = (): Array<MultiSelectOption> => {
        //returning an array of Baselines that are not already pushed down to any of the selected locations
        //comparison is done by Baseline name: app does not allow duplicate baseline names in the same subscription
        const selectedLocations = getSelectedLocations();
        const baselineNames = new Set(selectedLocations.flatMap((location) => location.baselines).map((baseline) => baseline?.name));

        return projectBaselines.filter(({ name }) => !baselineNames.has(name));
    };

    const scopeWarningModalBody =
        'Some locations were not removed from scope because the project has been published to them. Before removing locations from scope, consider filtering the Status column to a value of Published.';
    const scopeWarningModalFooter = (
        <div>
            <button type='button' className='btn btn-primary' data-dismiss='modal' onClick={closeRemoveScopeWarning}>
                Ok
            </button>
        </div>
    );

    const revokePushdownWarningModalBody =
        'Some locations/teams were not updated because the project has not been published to them. Before unpublishing, consider filtering the Status column to a value of Published.';
    const revokePushdownWarningModalFooter = (
        <div>
            <button type='button' className='btn btn-primary' data-dismiss='modal' onClick={closeRevokePushdownWarning}>
                Ok
            </button>
        </div>
    );

    const onholdWarningModalBody = `Some locations/teams were not updated because the project has not been published to them. Before selecting ${isHoldOperation ? 'On Hold' : 'Off Hold'
        }, consider filtering the Status column to a value of Published.`;

    const onholdWarningModalFooter = (
        <div>
            <button type='button' className='btn btn-primary' data-dismiss='modal' onClick={closeOnholdWarning}>
                Ok
            </button>
        </div>
    );

    const getStaticProjectLocationColumns = (): Array<ProjectLocationColumn> =>
        getStaticDefaultLocationColumns(
            typesOptions,
            projectLocationListFilters,
            statusOptions,
            activeOptions,
            zeroDateFilters,
            baselines,
            projectManagersFilters,
            brands,
            continents,
            managementCompanies,
            areas,
            regions,
            countries,
            statusBodyTemplate,
            zeroDateBodyTemplate,
            baselineBodyTemplate,
            isOnHoldTemplate,
            isActiveTemplate,
            actionBodyTemplate,
            filterChange,
            keyUpFilter,
            onStatusChange,
            onMultiSelectChange,
            onDropDownChange
        );

    useEffect(() => {
        const params = buildSearchRequest();
        search(params);
    }, [searchParams]);

    useEffect(() => {
        const specificOptionsToToggle = [
            { id: BulkOptions.AddBaseline.value, name: BulkOptions.AddBaseline.label },
            { id: BulkOptions.ChangeManager.value, name: BulkOptions.ChangeManager.label, icon: BulkOptions.ChangeManager.icon },
            { id: BulkOptions.ChangeZeroDate.value, name: BulkOptions.ChangeZeroDate.label, icon: BulkOptions.ChangeZeroDate.icon },
        ];

        const selectedLocations = getSelectedLocations();
        const draftCount = selectedLocations.filter((row) => row.isPushedDown == false).length;

        if (draftCount === 0) {
            // Add specific options if not already present
            setAvailableBulkOperations((prevOptions) => {
                const newOptions = specificOptionsToToggle.filter((option) => !prevOptions.some((existingOption) => existingOption.id === option.id));
                return [...prevOptions, ...newOptions];
            });
        } else {
            // Remove specific options
            setAvailableBulkOperations((prevOptions) =>
                prevOptions.filter((option) => !specificOptionsToToggle.some((specificOption) => specificOption.id === option.id))
            );
        }
    }, [selectedLocations, getSelectedLocations]);

    useEffect(() => {
        const fetchData = async () => {
            const [baselines, managers] = await Promise.all([formatBaselines(), formatProjectManagers()]);

            setProjectBaselines(baselines);
            setProjectManagers(managers);

            await formatLoadProjectLocationListFilters();

            setSelectedBulkOperation(BulkOptions.Publish.value);
        };

        fetchData();

        // Join a group based on subscription
        signalRService.joinGroup(projectData.projectKey);

        // Initialize the SignalR service and set up global event listeners
        signalRService.on('UpdateProjectLocationStatus', (projectLocation) => {
            setIsPublishing((prev) => {
                const updated = new Set(prev);
                updated.delete(projectLocation.locationId);
                return updated;
            });

            // Update the specific record in the locations state
            setLocations((prevLocations) => {
                // Find the index of the location with the matching ID
                const locationIndex = prevLocations.findIndex((loc) => loc.id === projectLocation.id);

                if (locationIndex !== -1) {
                    // Replace the existing record with the new one
                    const updatedLocations = [...prevLocations];
                    updatedLocations[locationIndex] = projectLocation;
                    return updatedLocations;
                }

                // If no matching record is found, return the existing locations
                return prevLocations;
            });
        });
    }, []);

    useEffect(() => {
        // Leaving this commented out for now, as it seems to solve the problem of the columns not being built but may have unintended consequences?
        // if (
        // 	projectLocationListFilters &&
        // 	projectLocationListFilters.baselines?.length > 0 &&
        // 	projectLocationListFilters.projectManagers?.length > 0 &&
        // 	zeroDateFilters?.length > 0
        // ) {
        buildColumnOptions();
    }, [userColumns, zeroDateFilters, projectLocationListFilters]);

    const renderColumns = () => {
        const cols = columns.map((col) => {
            const filterElement = col.filterElement;
            if (col.field === 'isActive') {
                return (
                    <Column
                        key={col.field}
                        sortable={col.isSortable}
                        field={col.field}
                        filter={true}
                        filterElement={filterElement}
                        header={col.header}
                        body={col.body}
                        style={col.style}
                    />
                );
            } else if (filterElement && col.isFeatureEnabled) {
                return (
                    <Column
                        key={col.field}
                        sortable={col.isSortable}
                        field={col.field}
                        filter={true}
                        filterElement={filterElement}
                        header={col.header}
                        body={col.body}
                        style={col.style}
                    />
                );
            } else if (col.isFeatureEnabled) {
                return <Column style={col.style} key={col.field} sortable={col.isSortable} field={col.field} header={col.header} body={col.body} />;
            } else {
                return null;
            }
        });

        return cols;
    };

    return (
        <div className='project-locations publish-content'>
            <div className='d-flex justify-content-end'>
                <Button
                    type='button'
                    icon='fa-regular fa-circle-plus'
                    className='btn pacer61-primary-purple-btn my-3'
                    onClick={() => {
                        addProjectLocationsModal.current?.show();
                    }}>
                    <span className='mx-2'>Add Location/Team to Scope</span>
                </Button>
            </div>
            <DataTable
                filterDisplay='row'
                value={locations}
                header={headerComponent}
                className='datatable-wrapper p-datatable-sm p-datatable-striped'
                loading={isLoading}
                sortField='locationName'
                sortOrder={1}
                reorderableColumns
                onColReorder={(e) => onColumnReorder(e)}
                selectionMode='checkbox'
                selection={selectedLocations}
                onSelectionChange={onSelectionChange}
                dataKey='id'
                showSelectAll={false}
                scrollable
                scrollHeight='flex'>
                <Column selectionMode='multiple' headerStyle={{ width: '3rem' }}></Column>
                {renderColumns()}
            </DataTable>
            <Paginator
                leftContent={<span>Rows per page:</span>}
                rows={searchParams.pageSize}
                totalRecords={totalCount}
                first={(searchParams.pageNumber - 1) * searchParams.pageSize}
                rowsPerPageOptions={[20, 50, 100]}
                onPageChange={onPageChange}
                template='RowsPerPageDropdown CurrentPageReport FirstPageLink PrevPageLink NextPageLink LastPageLink'
                currentPageReportTemplate='{first}-{last} of {totalRecords}'
            />
            {isColumnDrawerOpen && (
                <ColumnsDrawer onComplete={onDrawerClose} onColumnToggle={onColumnToggle} columnOptions={columnOptions} columns={columns} />
            )}
            {isAddToScopeDrawerOpen && (
                <AddToScopeDrawer
                    subscriptionId={subscriptionId}
                    projectKey={projectData.projectKey}
                    onCancel={onAddScopeCancel}
                    onComplete={onAddScopeComplete}
                    projectLocationActions={projectLocationActions}
                />
            )}
            {isChangeProjectManagerDrawerOpen && (
                <ChangeProjectManagerDrawer
                    projectManagers={projectManagersForDrawer}
                    selectedProjectLocations={getSelectedLocations()}
                    projectKey={projectData.projectKey}
                    onCancel={onChangeManagerCancel}
                    onComplete={onChangeManagerComplete}
                    isLoading={isLoadingFormFilters}
                />
            )}
            {isAddBaselineDrawerOpen && (
                <AddBaselineDrawer
                    baselines={getAdditionalBaselines()}
                    selectedProjectLocations={getSelectedLocations()}
                    projectKey={projectData.projectKey}
                    onCancel={onAddBaselineCancel}
                    onComplete={onAddBaselineComplete}
                    isLoading={isLoadingFormFilters}
                />
            )}
            {showRemoveScopeWarning && (
                <Modal
                    id='pushedDown'
                    title='Skipped Locations'
                    body={scopeWarningModalBody}
                    onCloseClick={closeRemoveScopeWarning}
                    customFooter={scopeWarningModalFooter}
                    style={{ top: '30%' }}
                />
            )}
            {showRevokePushdownWarning && (
                <Modal
                    id='revokePushdownWarning'
                    title='Skipped Locations'
                    body={revokePushdownWarningModalBody}
                    onCloseClick={closeRevokePushdownWarning}
                    customFooter={revokePushdownWarningModalFooter}
                    style={{ top: '30%' }}
                />
            )}
            {showOnholdWarning && (
                <Modal
                    id='onholdWarning'
                    title='Skipped Locations'
                    body={onholdWarningModalBody}
                    onCloseClick={closeOnholdWarning}
                    customFooter={onholdWarningModalFooter}
                    style={{ top: '30%' }}
                />
            )}

            <Modal61
                ref={infoWindow}
                body={
                    <InfoWindowBody>
                        <div className='row justify-content-center'>
                            <InfoWindowIcon>
                                <i className='fa-regular fa-paper-plane'></i>
                            </InfoWindowIcon>
                        </div>
                        <div className='row justify-content-center'>
                            <InfoWindowIconDescription>
                                Almost Complete!
                                <br />
                                Ready to Publish?
                            </InfoWindowIconDescription>
                        </div>

                        <InfoWindowTopicTitle>What is a Project Zero Date?</InfoWindowTopicTitle>
                        <InfoWindowTopicBody>
                            In order to publish a project to a location, you will need to set a Project Zero Date. This is the ‘go live’ or ‘launch
                            date’ of a selected location.
                        </InfoWindowTopicBody>

                        <InfoWindowTopicTitle>What does publishing a project mean?</InfoWindowTopicTitle>
                        <InfoWindowTopicBody>
                            Publishing a project will allow the selected location(s) to see this project within their Dashboard. Prior to publication,
                            the project will live in a draft state. You can always make changes post publication.
                        </InfoWindowTopicBody>
                    </InfoWindowBody>
                }
                customFooter={
                    <>
                        <button type='button' className='btn btn-primary' data-dismiss='modal' onClick={closeInfoWindow}>
                            Go to Publish Page
                        </button>
                    </>
                }
                title={''}
                primaryButtonText={''}
                onPrimaryClick={undefined}
                onSecondaryClick={undefined}
                onCloseClick={closeInfoWindow}
                isLoading={false}
                isCentered={true}
            />

            <Modal61
                ref={publishModal}
                title='Publish Project'
                body={
                    <>
                        <div className='row'>
                            <div className='col-md-6'>
                                <div>
                                    <label className='title'>Baseline(s):</label>
                                </div>
                                <MultiSelect
                                    optionLabel='name'
                                    required={true}
                                    className='w-100 pacer-multiselect'
                                    value={selectedBaselines}
                                    options={projectBaselines}
                                    display='chip'
                                    onChange={(e) => {
                                        setSelectedBaselines(e.value);
                                    }}
                                />
                            </div>
                            <div className='col-md-6'>
                                <div>
                                    <label className='title'>Zero Date:</label>
                                </div>
                                <Calendar
                                    value={zeroDate}
                                    className='pacer-calendar'
                                    onChange={(e) => setZeroDate(e.value ?? null)}
                                    placeholder='When is this project complete?'
                                    dateFormat='dd M yy'
                                    showIcon
                                    iconPos='left'
                                    required={true}
                                />
                            </div>
                        </div>
                        <div className='row'>
                            <div className='col-12 text-end'>
                                <button className='btn btn-link mt-3 mb-3' onClick={toggleAdvancedOptions}>
                                    {showAdvancedOptions ? (
                                        <>
                                            <i className='pi pi-minus'></i> Hide Advanced Options
                                        </>
                                    ) : (
                                        <>
                                            <i className='pi pi-plus'></i> Show Advanced Options
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>

                        {showAdvancedOptions && (
                            <div className='row'>
                                <div className='col-md-6'>
                                    <div>
                                        <label className='title'>Project Owner:</label>
                                    </div>
                                    <Dropdown
                                        value={selectedProjectOwner}
                                        options={projectManagers}
                                        className='w-100'
                                        onChange={(e) => setSelectedProjectOwner(e.value)}
                                        placeholder='Select Project Owner'
                                    />
                                </div>

                                <div className='col-md-6'>
                                    <label className='title'>Send Publish Notification Email</label>
                                    <br />
                                    <div className='custom-container-radio'>
                                        <RadioGroup
                                            defaultValue={'true'}
                                            value={sendEmailOnPublish ? 'true' : 'false'}
                                            options={[
                                                { value: 'false', label: 'No' },
                                                { value: 'true', label: 'Yes' },
                                            ]}
                                            onValueChange={(value) => setSendEmailOnPublish(value === 'true')}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                }
                customFooter={
                    <>
                        <button
                            type='button'
                            className='btn btn-primary'
                            data-bs-dismiss='modal'
                            onClick={onPublishModal}
                            title={!isPublishFormValid ? 'Baseline and Zero Date are required' : undefined}
                            disabled={getSelectedLocations()
                                .map((loc) => loc.locationId)
                                .filter((id): id is number => id !== undefined)
                                .some((id) => isPublishing.has(id)) || !isPublishFormValid}>
                            Publish
                        </button>
                    </>
                }
                primaryButtonText='Publish'
                onPrimaryClick={undefined}
                onSecondaryClick={undefined}
                onCloseClick={closePublishModal}
                isLoading={false}
                isCentered={false}
            />

            <Modal61
                ref={addProjectLocationsModal}
                title='Select locations'
                body={
                    <ProjectLocations
                        projectData={projectData}
                        setProjectData={(updatedProjectData) => {
                            if (!updatedProjectData) {
                                return;
                            }
                            const nextProjectData = typeof updatedProjectData === 'function' ? updatedProjectData(projectData) : updatedProjectData;
                            if (nextProjectData && typeof nextProjectData === 'object') {
                                Object.assign(projectData, nextProjectData);
                            }
                        }}
                        setIsLoading={setIsLoading}
                        subscriptionId={subscriptionId}
                        setProjectData={() => { return; }}
                        locationsState={{
                            availableLocations: availLocations,
                            selectedLocations: selectedLocationsModal,
                            setAvailableLocations: setAvailLocations,
                            setSelectedLocations: setSelectedLocationsModal,
                        }}
                    ></ProjectLocations>
                }
                customFooter={
                    <>
                        <PacerButton type='button' text='Add Locations' bootstrapClass='primary' onClick={addLocationsToScope} isModalDismiss={true} />
                    </>
                }
                primaryButtonText='Publish'
                onPrimaryClick={undefined}
                onSecondaryClick={undefined}
                onCloseClick={closeAddLocationsModal}
                isLoading={false}
                isCentered={false}
            />

            <Modal61
                ref={changeZeroDateModal}
                title='Change Zero Date'
                body={
                    <ChangeZeroDateDrawerModal
                        selectedProjectLocations={getSelectedLocations()}
                        projectKey={projectData.projectKey}
                        onCancel={onChangeZeroDateCancel}
                        onComplete={onChangeZeroDateCompleteAndClear}
                        onDataChange={onChangeZeroDateDataChange}
                    />
                }
                primaryButtonText='Save'
                secondaryButtonText='Save'
                onPrimaryClick={onChangeZeroDateCompleteAndClear}
                onSecondaryClick={onChangeZeroDateCompleteAndClear}
                onCloseClick={() => {
                    onChangeZeroDateCancel();
                }}
                isLoading={false}
            />

            <RemoveBaselineModal modalRef={removeBaselineModal} projectLocation={getSelectedLocations()[0]} closeModal={onRemoveBaselineModalHide} />
        </div>
    );
};

const mapDispatchToProps = (dispatch) => {
    return {
        projectLocationActions: bindActionCreators(projectLocationActions, dispatch),
    };
};

export default connect(mapDispatchToProps)(Locations);
